import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/hooks/useWorkspace";

export interface ChatChannel {
  id: string;
  name: string;
  description: string | null;
  channel_type: "general" | "client" | "project";
  client_id: string | null;
  workspace_id: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  client?: { id: string; name: string; company: string | null } | null;
}

export interface ChatMessage {
  id: string;
  channel_id: string;
  user_id: string;
  content: string;
  is_ai_response: boolean;
  workspace_id: string;
  created_at: string;
  updated_at: string;
  profile?: { id: string; full_name: string | null; avatar_url: string | null } | null;
}

export function useChat(channelId?: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  const { currentWorkspace } = useWorkspace();
  const channelsQueryKey = ["chat-channels", currentWorkspace?.id] as const;
  const messagesQueryKey = ["chat-messages", currentWorkspace?.id, channelId] as const;

  const { data: channels = [], isLoading: channelsLoading } = useQuery({
    queryKey: channelsQueryKey,
    enabled: !!currentWorkspace?.id,
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];

      const { data, error } = await supabase
        .from("chat_channels")
        .select("*, client:clients(id, name, company)")
        .eq("workspace_id", currentWorkspace.id)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return (data ?? []) as ChatChannel[];
    },
  });

  const { data: messages = [], isLoading: messagesLoading } = useQuery({
    queryKey: messagesQueryKey,
    enabled: !!currentWorkspace?.id && !!channelId,
    queryFn: async () => {
      if (!currentWorkspace?.id || !channelId) return [];

      const { data, error } = await supabase
        .from("chat_messages")
        .select("*, profile:profiles(id, full_name, avatar_url)")
        .eq("workspace_id", currentWorkspace.id)
        .eq("channel_id", channelId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return (data ?? []) as ChatMessage[];
    },
  });

  useEffect(() => {
    if (!currentWorkspace?.id) return;

    const workspaceId = currentWorkspace.id;

    const channelsChannel = supabase
      .channel(`chat-channels-${workspaceId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "chat_channels",
          filter: `workspace_id=eq.${workspaceId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: channelsQueryKey });
        },
      )
      .subscribe();

    const messagesChannel = supabase
      .channel(`chat-messages-${workspaceId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "chat_messages",
          filter: `workspace_id=eq.${workspaceId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: messagesQueryKey });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channelsChannel);
      supabase.removeChannel(messagesChannel);
    };
  }, [channelsQueryKey, currentWorkspace?.id, messagesQueryKey, queryClient]);

  const createChannel = useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string | null;
      channel_type?: ChatChannel["channel_type"];
      client_id?: string | null;
    }) => {
      if (!currentWorkspace?.id) {
        throw new Error("Workspace não encontrado");
      }

      const { data, error } = await supabase
        .from("chat_channels")
        .insert({
          name: input.name,
          description: input.description ?? null,
          channel_type: input.channel_type ?? "general",
          client_id: input.client_id ?? null,
          workspace_id: currentWorkspace.id,
          created_by: user?.id ?? null,
        })
        .select("*, client:clients(id, name, company)")
        .single();

      if (error) throw error;
      return data as ChatChannel;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: channelsQueryKey });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao criar canal",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateChannel = useMutation({
    mutationFn: async (input: {
      id: string;
      updates: Partial<Pick<ChatChannel, "name" | "description">>;
    }) => {
      if (!currentWorkspace?.id) {
        throw new Error("Workspace não encontrado");
      }

      const { data, error } = await supabase
        .from("chat_channels")
        .update(input.updates)
        .eq("id", input.id)
        .eq("workspace_id", currentWorkspace.id)
        .select("*, client:clients(id, name, company)")
        .single();

      if (error) throw error;
      return data as ChatChannel;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: channelsQueryKey });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao atualizar canal",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteChannel = useMutation({
    mutationFn: async (id: string) => {
      if (!currentWorkspace?.id) {
        throw new Error("Workspace não encontrado");
      }

      const { error } = await supabase
        .from("chat_channels")
        .delete()
        .eq("id", id)
        .eq("workspace_id", currentWorkspace.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: channelsQueryKey });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao excluir canal",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const sendMessage = useMutation({
    mutationFn: async (input: { content: string; is_ai_response?: boolean }) => {
      if (!currentWorkspace?.id) {
        throw new Error("Workspace não encontrado");
      }

      if (!channelId) {
        throw new Error("Canal não selecionado");
      }

      if (!user?.id) {
        throw new Error("Usuário não autenticado");
      }

      const { data, error } = await supabase
        .from("chat_messages")
        .insert({
          channel_id: channelId,
          user_id: user.id,
          content: input.content,
          is_ai_response: input.is_ai_response ?? false,
          workspace_id: currentWorkspace.id,
        })
        .select("*, profile:profiles(id, full_name, avatar_url)")
        .single();

      if (error) throw error;
      return data as ChatMessage;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagesQueryKey });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao enviar mensagem",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    channels,
    messages,
    channelsLoading,
    messagesLoading,
    createChannel,
    updateChannel,
    sendMessage,
    deleteChannel,
    currentWorkspace,
  };
}
